import { useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SERVICES } from "@/data/content";
import { SERVICES_PAGE } from "@/constants/testIds";

export default function Services() {
  const { t } = useTranslation();
  const location = useLocation();
  const refs = useRef({});

  useEffect(() => {
    const focus = location.state && location.state.focus;
    if (focus && refs.current[focus]) {
      setTimeout(() => {
        refs.current[focus].scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [location.state]);

  return (
    <div className="relative">
      {/* HEADER */}
      <section className="relative overflow-hidden pt-24 md:pt-36 pb-20">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_50%_at_20%_10%,rgba(192,94,76,0.12),transparent_70%)]" />
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("services.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("services.heroTitle1")}<br />
              {t("services.heroTitle2")} <span className="italic text-clay-400">{t("services.heroTitleItalic")}</span>.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="body-lg mt-10 max-w-3xl">
              {t("services.heroDescription")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* NAV RAIL */}
      <section className="border-y border-white/10 bg-ink-900/40 sticky top-20 z-30 backdrop-blur-xl">
        <div className="container-x py-4 overflow-x-auto">
          <ul className="flex items-center gap-8 min-w-max">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-xs uppercase tracking-widest2 text-bone-100/70 hover:text-clay-400 transition-colors"
                >
                  <span className="text-clay-400 mr-2">{s.number}</span>
                  {t(`servicesData.${s.i18nKey}.title`)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICE SECTIONS */}
      <section className="py-16 md:py-24">
        <div className="container-x space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const learnMoreTestId = `${SERVICES_PAGE.learnMorePrefix}-${s.id}`;
            const cardTestId = `${SERVICES_PAGE.cardPrefix}-${s.id}`;
            const external = s.external;

            const LearnMore = external ? (
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={learnMoreTestId}
                className="btn-primary"
              >
                {t("common.learnMore")}
                <ArrowUpRight size={16} />
              </a>
            ) : (
              <Link
                to="/contact"
                data-testid={learnMoreTestId}
                className="btn-primary"
              >
                {t("services.discussService")}
                <ArrowRight size={16} />
              </Link>
            );

            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={cardTestId}
                ref={(el) => (refs.current[s.id] = el)}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 scroll-mt-40"
              >
                <div className="md:col-span-4">
                  <Reveal>
                    <div className="font-display text-6xl md:text-7xl text-clay-400/80 leading-none mb-6">
                      {s.number}
                    </div>
                  </Reveal>
                  <Reveal delay={80}>
                    <h2 className="display-h2 leading-tight">{t(`servicesData.${s.i18nKey}.title`)}</h2>
                  </Reveal>
                  <Reveal delay={140}>
                    <p className="text-clay-400 mt-6 text-sm uppercase tracking-widest2 font-semibold">
                      {t(`servicesData.${s.i18nKey}.tagline`)}
                    </p>
                  </Reveal>
                </div>

                <div className="md:col-span-8 md:pt-4">
                  <Reveal delay={120}>
                    <p className="body-lg mb-10">
                      {t(`servicesData.${s.i18nKey}.description`)}
                    </p>
                  </Reveal>

                  <Reveal delay={180}>
                    <p className="text-xs uppercase tracking-widest2 text-bone-200/50 mb-5">
                      {t("services.capabilitiesLabel")}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
                      {s.capabilities.map((cap) => (
                        <li key={cap} className="flex items-start gap-3 text-bone-100/90">
                          <Check size={16} className="text-clay-400 mt-1 shrink-0" />
                          <span>{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>

                  <Reveal delay={240}>
                    <div className="flex flex-wrap items-center gap-4">
                      {LearnMore}
                      {external && (
                        <span className="text-xs uppercase tracking-widest2 text-bone-200/50">
                          {t("services.opensExternal")}
                        </span>
                      )}
                    </div>
                  </Reveal>
                </div>

                {i < SERVICES.length - 1 && (
                  <div className="md:col-span-12">
                    <div className="mt-6 md:mt-10 border-b border-white/10" />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <Reveal>
            <div className="border border-white/10 rounded-lg p-10 md:p-16 bg-ink-800/40 backdrop-blur">
              <p className="eyebrow mb-6">{t("services.ctaEyebrow")}</p>
              <h2 className="display-h2 max-w-3xl">
                {t("services.ctaTitle")}
              </h2>
              <div className="mt-10">
                <Link to="/contact" className="btn-primary">
                  {t("common.bookDiagnostic")}
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
